import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppButton,
  AutocompleteDropdown,
  Box,
  Controller,
  ControllerWrapper,
  CrossIcon,
  DateInput,
  FieldWrapper,
  FormFieldsContainer,
  Inscription,
  Label,
  MaskTextInput,
  Option,
  PlusIcon,
  RegularField,
  useFieldArray,
  useForm,
  useWatch,
  ZipCodeInput,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT, REQUIRED_TEXT } from '@/common/constants';
import {
  DATE_FORMAT_VALIDATOR,
  FUTURE_DATE_TEXT,
  getZipCodeValidationErrorMessage,
  TODAY,
  WRONG_DATE_TEXT,
} from '@/common/validators';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';

import { AppBeneficiaryField, AppBeneficiaryForm } from './common';
import SpecialStatesWarning from './SpecialStatesWarning';
import { BeneficiaryRelationship } from './useBeneficiaryRelationshipsTypes.query';
import useCreateUpdateBeneficiariesQuery, {
  formatFormDataToPayload,
  getIsTaxIdMode,
} from './useCreateUpdateBeneficiaries.query';
import { Beneficiary, BeneficiaryType } from './useGetBeneficiaries.query';
import useHasWarningAddressState from './useHasWarningAddressState';

const maxAllocationPercentage = 100;
const MAX_SSN_LENGTH = 20;
const MAX_SSN_LENGTH_ERROR = 'Entered value is too long';
const getSsnValidationTextError = (ssn?: string) => {
  if (!ssn) return REQUIRED_TEXT;
  if (ssn.length > MAX_SSN_LENGTH) return MAX_SSN_LENGTH_ERROR;
  return '';
};
const getAllocationPercentageValidationError = (
  data: AppBeneficiaryForm[],
  allocationPercentage?: string,
  beneficiaryType?: BeneficiaryType
) => {
  if (!allocationPercentage) return REQUIRED_TEXT;
  if (!Number(allocationPercentage)) return 'Value must be greater than zero';

  const allAllocationPercentage = data.reduce(
    (acc, val) => acc + Number(val[AppBeneficiaryField.allocationPercentage]),
    0
  );
  const hasAllocationPercentageError = allAllocationPercentage !== maxAllocationPercentage;

  if (hasAllocationPercentageError) {
    return `Allocation across all ${beneficiaryType?.toLowerCase()} beneficiaries must total ${maxAllocationPercentage}%`;
  }

  return '';
};
const getDateValidationErrorText = (date?: string) => {
  if (!date) return REQUIRED_TEXT;
  if (!DATE_FORMAT_VALIDATOR(date)) return WRONG_DATE_TEXT;
  if (dayjs(date).valueOf() > dayjs(TODAY).valueOf()) return FUTURE_DATE_TEXT;

  return '';
};

const StyledFieldsList = styled(Box)`
  > div:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacings.spacing16};
  }
`;
const InputWrapper = styled(Box)`
  > div {
    width: 320px;
  }
`;

const fieldArrayName = 'updatedBeneficiaries';
const getPath = (fieldName: string, index: number) => `${fieldArrayName}.${index}.${fieldName}`;

const formatToAppendBeneficiary = (beneficiary?: Beneficiary) => ({
  [AppBeneficiaryField.id]: beneficiary?.id || '',
  [AppBeneficiaryField.relationshipType]: beneficiary?.relationshipType || '',
  [AppBeneficiaryField.name]: beneficiary?.name || '',
  [AppBeneficiaryField.taxId]: beneficiary?.taxId || '',
  ...(beneficiary?.birthOn
    ? {
        [AppBeneficiaryField.birthOn]: dayjs(beneficiary?.birthOn).format(DEFAULT_DATE_FORMAT),
      }
    : {
        [AppBeneficiaryField.birthOn]: '',
      }),
  [AppBeneficiaryField.ssn]: beneficiary?.ssn || '',
  [AppBeneficiaryField.line1]: beneficiary?.address?.line1 || '',
  [AppBeneficiaryField.line2]: beneficiary?.address?.line2 || '',
  [AppBeneficiaryField.city]: beneficiary?.address?.city || '',
  [AppBeneficiaryField.state]: beneficiary?.address?.state || '',
  [AppBeneficiaryField.zipcode]: beneficiary?.address?.zipcode || '',
  [AppBeneficiaryField.allocationPercentage]: beneficiary?.allocationPercentage || '',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const hasErrors = (errors?: any) =>
  ((errors?.[fieldArrayName] || []) as any[])?.some((formItems) =>
    Object.keys(formItems)?.some((key) => Boolean(formItems[key]?.message))
  );

interface Props {
  onClose: () => void;
  beneficiaries?: Beneficiary[];
  beneficiaryType?: BeneficiaryType;
  relationshipsTypes?: Option[];
  states?: Option[];
}

const AddBeneficiaryForm: React.FC<Props> = ({
  onClose,
  beneficiaries,
  beneficiaryType,
  relationshipsTypes,
  states,
}) => {
  const beneficiaryTypeTitle = useMemo(() => capitalize(beneficiaryType), [beneficiaryType]);
  const isEditMode = useMemo(() => Boolean(beneficiaries?.length), [beneficiaries]);
  const [isErrorShown, setIsErrorShown] = useState(false);
  const hasWarningState = useHasWarningAddressState();
  const hasSpecialStatesWarningMessage = useMemo(
    () => hasWarningState && beneficiaryType === BeneficiaryType.PRIMARY,
    [hasWarningState, beneficiaryType]
  );
  const {
    control,
    formState: { errors },
    setError,
    getValues,
  } = useForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });
  const allData: AppBeneficiaryForm[] = useWatch({
    control,
    name: fieldArrayName,
  });
  const { handleAddPermanentSnackbar } = useSnackbar();
  const onSuccess = useCallback(() => {
    onClose();
    handleAddPermanentSnackbar({
      text: `${beneficiaryTypeTitle} beneficiaries has been ${isEditMode ? 'edited' : 'added'}`,
      state: SnackbarState.positive,
      closeIcon: true,
    });
  }, [onClose, isEditMode, beneficiaryTypeTitle, handleAddPermanentSnackbar]);

  const { mutateAsync } = useCreateUpdateBeneficiariesQuery({ isEditMode, onSuccess });
  const onSubmit = useCallback(() => {
    if (!isErrorShown) setIsErrorShown(true);
    if (hasErrors(errors)) return;
    mutateAsync(formatFormDataToPayload(allData, beneficiaryType));
  }, [allData, errors, setIsErrorShown, isErrorShown, mutateAsync, beneficiaryType]);

  useEffect(() => {
    if (Array.isArray(allData)) {
      allData.forEach((item, index) => {
        const hasTaxId = getIsTaxIdMode(item?.relationshipType);

        setError(getPath(AppBeneficiaryField.name, index), { message: item?.name ? '' : REQUIRED_TEXT });
        setError(getPath(AppBeneficiaryField.taxId, index), {
          message: hasTaxId ? (item?.taxId ? '' : REQUIRED_TEXT) : '',
        });
        setError(getPath(AppBeneficiaryField.birthOn, index), {
          message: hasTaxId ? '' : getDateValidationErrorText(item?.birthOn),
        });
        setError(getPath(AppBeneficiaryField.ssn, index), {
          message: hasTaxId ? '' : getSsnValidationTextError(item?.ssn),
        });
        setError(getPath(AppBeneficiaryField.line1, index), { message: item?.line1 ? '' : REQUIRED_TEXT });
        setError(getPath(AppBeneficiaryField.state, index), { message: item?.state ? '' : REQUIRED_TEXT });
        setError(getPath(AppBeneficiaryField.city, index), { message: item?.city ? '' : REQUIRED_TEXT });
        setError(getPath(AppBeneficiaryField.relationshipType, index), {
          message: item?.relationshipType ? '' : REQUIRED_TEXT,
        });
        setError(getPath(AppBeneficiaryField.zipcode, index), {
          message: item?.zipcode ? getZipCodeValidationErrorMessage(item.zipcode) : REQUIRED_TEXT,
        });
        setError(getPath(AppBeneficiaryField.allocationPercentage, index), {
          message: getAllocationPercentageValidationError(
            allData,
            item?.allocationPercentage,
            beneficiaryType
          ),
        });
      });
    }
  }, [setError, allData, beneficiaryType]);

  const typeTitle = useMemo(() => capitalize(beneficiaryType), [beneficiaryType]);

  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;

    initialized.current = true;
    if (beneficiaries?.length) {
      beneficiaries.forEach((item) => append(formatToAppendBeneficiary(item)));
    } else {
      append(formatToAppendBeneficiary());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isWarningStateRelationship = useCallback(
    (index: number) => {
      const values = getValues(fieldArrayName);
      const relationship = values?.[index]?.relationshipType;
      return relationship !== BeneficiaryRelationship.SPOUSE;
    },
    [getValues]
  );

  const isTaxIdMode = useCallback(
    (index: number) => {
      const values = getValues(fieldArrayName);
      const relationship = values?.[index]?.relationshipType;
      return getIsTaxIdMode(relationship);
    },
    [getValues]
  );

  return (
    <>
      <Box data-testId="AddBeneficiaryForm" background="module" pad="spacing24" round>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            pad="spacing24"
            margin={{ bottom: 'spacing24' }}
            background="canvas"
            round="container1Round"
            border={{
              size: 'small',
              color: 'border2',
            }}
          >
            {fields.length > 1 ? (
              <Box
                data-testId={`Beneficiary_${index + 1}`}
                direction="row"
                justify="between"
                margin={{ bottom: 'spacing16' }}
              >
                <Inscription color="textTitle" size="20px" lineHeight="28px">
                  Beneficiary {index + 1}
                </Inscription>

                <Box
                  data-testId={`Remove_${index}`}
                  direction="row"
                  align="center"
                  height="20px"
                  onClick={() => remove(index)}
                >
                  <Inscription color="danger">Remove</Inscription>
                  <Box margin={{ left: 'spacing6' }} align="center">
                    <CrossIcon color="danger" size="10px" />
                  </Box>
                </Box>
              </Box>
            ) : null}

            <StyledFieldsList>
              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.relationshipType} showRequireIcon isNarrow>
                      Relationship
                    </Label>
                    <ControllerWrapper style={{ flex: 'none' }}>
                      <Controller
                        name={getPath(AppBeneficiaryField.relationshipType, index)}
                        control={control}
                        render={({ field }) => (
                          <AutocompleteDropdown
                            testId={`${AppBeneficiaryField.relationshipType}_${index}`}
                            name={AppBeneficiaryField.relationshipType}
                            onChange={field.onChange}
                            options={relationshipsTypes}
                            value={field.value}
                            defaultValue={field.value}
                            style={{ width: '320px' }}
                            placeholder="Select relationship"
                            {...(isErrorShown
                              ? {
                                  errorText:
                                    // @ts-ignore
                                    errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.relationshipType]
                                      ?.message,
                                }
                              : {})}
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
                {hasSpecialStatesWarningMessage && isWarningStateRelationship(index) ? (
                  <Box margin={{ top: 'spacing16' }}>
                    <SpecialStatesWarning />
                  </Box>
                ) : null}
              </FormFieldsContainer>

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.name} showRequireIcon isNarrow>
                      Name
                    </Label>
                    <ControllerWrapper
                      style={{ flex: 'none' }}
                      {...(isErrorShown
                        ? {
                            // @ts-ignore
                            errorText: errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.name]?.message,
                          }
                        : {})}
                    >
                      <Controller
                        name={getPath(AppBeneficiaryField.name, index)}
                        control={control}
                        render={({ field }) => (
                          <RegularField
                            name={AppBeneficiaryField.name}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Enter name"
                            isError={
                              // @ts-ignore
                              errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.name]?.message &&
                              isErrorShown
                            }
                            style={{ width: '320px' }}
                            editMode
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>

              {isTaxIdMode(index) ? (
                <FormFieldsContainer>
                  <FieldWrapper>
                    <Box direction="row" align="center" justify="between" width="100%">
                      <Label name={AppBeneficiaryField.taxId} showRequireIcon isNarrow>
                        Tax ID
                      </Label>
                      <ControllerWrapper
                        style={{ flex: 'none' }}
                        {...(isErrorShown
                          ? {
                              errorText:
                                // @ts-ignore
                                errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.taxId]?.message,
                            }
                          : {})}
                      >
                        <Controller
                          name={getPath(AppBeneficiaryField.taxId, index)}
                          control={control}
                          render={({ field }) => (
                            <RegularField
                              name={AppBeneficiaryField.taxId}
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Enter Tax ID"
                              isError={
                                // @ts-ignore
                                errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.taxId]?.message &&
                                isErrorShown
                              }
                              style={{ width: '320px' }}
                              editMode
                            />
                          )}
                        />
                      </ControllerWrapper>
                    </Box>
                  </FieldWrapper>
                </FormFieldsContainer>
              ) : (
                <>
                  <FormFieldsContainer>
                    <FieldWrapper>
                      <Box direction="row" align="center" justify="between" width="100%">
                        <Label name={AppBeneficiaryField.birthOn} showRequireIcon isNarrow>
                          Date of birth
                        </Label>
                        <ControllerWrapper
                          style={{ flex: 'none' }}
                          {...(isErrorShown
                            ? {
                                errorText:
                                  // @ts-ignore
                                  errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.birthOn]?.message,
                              }
                            : {})}
                        >
                          <Controller
                            name={getPath(AppBeneficiaryField.birthOn, index)}
                            control={control}
                            render={({ field }) => (
                              <InputWrapper>
                                <DateInput
                                  isWide
                                  name={AppBeneficiaryField.birthOn}
                                  testId={`${AppBeneficiaryField.birthOn}_${index}`}
                                  maxDate={dayjs()}
                                  isError={
                                    // @ts-ignore
                                    errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.birthOn]
                                      ?.message && isErrorShown
                                  }
                                  placeholder="Enter date of birth"
                                  onChange={field.onChange}
                                  value={field.value}
                                  isPicker
                                />
                              </InputWrapper>
                            )}
                          />
                        </ControllerWrapper>
                      </Box>
                    </FieldWrapper>
                  </FormFieldsContainer>

                  <FormFieldsContainer>
                    <FieldWrapper>
                      <Box direction="row" align="center" justify="between" width="100%">
                        <Label name={AppBeneficiaryField.ssn} showRequireIcon isNarrow>
                          SSN
                        </Label>
                        <ControllerWrapper
                          style={{ flex: 'none' }}
                          {...(isErrorShown
                            ? {
                                errorText:
                                  // @ts-ignore
                                  errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.ssn]?.message,
                              }
                            : {})}
                        >
                          <Controller
                            name={getPath(AppBeneficiaryField.ssn, index)}
                            control={control}
                            render={({ field }) => (
                              <RegularField
                                name={AppBeneficiaryField.ssn}
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Enter SSN"
                                isError={
                                  // @ts-ignore
                                  errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.ssn]?.message &&
                                  isErrorShown
                                }
                                style={{ width: '320px' }}
                                editMode
                              />
                            )}
                          />
                        </ControllerWrapper>
                      </Box>
                    </FieldWrapper>
                  </FormFieldsContainer>
                </>
              )}

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.line1} showRequireIcon isNarrow>
                      Address line 1
                    </Label>
                    <ControllerWrapper
                      style={{ flex: 'none' }}
                      {...(isErrorShown
                        ? {
                            errorText:
                              // @ts-ignore
                              errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.line1]?.message,
                          }
                        : {})}
                    >
                      <Controller
                        name={getPath(AppBeneficiaryField.line1, index)}
                        control={control}
                        render={({ field }) => (
                          <RegularField
                            name={AppBeneficiaryField.line1}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Enter address"
                            isError={
                              // @ts-ignore
                              errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.line1]?.message &&
                              isErrorShown
                            }
                            style={{ width: '320px' }}
                            editMode
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.line2} isNarrow>
                      Address line 2
                    </Label>
                    <ControllerWrapper style={{ flex: 'none' }}>
                      <Controller
                        name={getPath(AppBeneficiaryField.line2, index)}
                        control={control}
                        render={({ field }) => (
                          <RegularField
                            name={AppBeneficiaryField.line2}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Enter address"
                            isError={
                              // @ts-ignore
                              errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.line2]?.message &&
                              isErrorShown
                            }
                            style={{ width: '320px' }}
                            editMode
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.city} showRequireIcon isNarrow>
                      City
                    </Label>
                    <ControllerWrapper
                      style={{ flex: 'none' }}
                      {...(isErrorShown
                        ? {
                            // @ts-ignore
                            errorText: errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.city]?.message,
                          }
                        : {})}
                    >
                      <Controller
                        name={getPath(AppBeneficiaryField.city, index)}
                        control={control}
                        render={({ field }) => (
                          <RegularField
                            name={AppBeneficiaryField.city}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Enter city"
                            isError={
                              // @ts-ignore
                              errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.city]?.message &&
                              isErrorShown
                            }
                            style={{ width: '320px' }}
                            editMode
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.state} showRequireIcon isNarrow>
                      Select state
                    </Label>
                    <ControllerWrapper style={{ flex: 'none' }}>
                      <Controller
                        name={getPath(AppBeneficiaryField.state, index)}
                        control={control}
                        render={({ field }) => (
                          <AutocompleteDropdown
                            testId={`${AppBeneficiaryField.state}_${index}`}
                            name={AppBeneficiaryField.state}
                            onChange={field.onChange}
                            options={states}
                            defaultValue={field.value}
                            value={field.value}
                            style={{ width: '320px' }}
                            placeholder="Select relationship"
                            {...(isErrorShown
                              ? {
                                  errorText:
                                    // @ts-ignore
                                    errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.state]?.message,
                                }
                              : {})}
                          />
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Label name={AppBeneficiaryField.zipcode} showRequireIcon isNarrow>
                      ZIP code
                    </Label>
                    <ControllerWrapper
                      style={{ flex: 'none' }}
                      {...(isErrorShown
                        ? {
                            errorText:
                              // @ts-ignore
                              errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.zipcode]?.message,
                          }
                        : {})}
                    >
                      <Controller
                        name={getPath(AppBeneficiaryField.zipcode, index)}
                        control={control}
                        render={({ field }) => (
                          <InputWrapper>
                            <ZipCodeInput
                              name={AppBeneficiaryField.zipcode}
                              testId={getPath(AppBeneficiaryField.zipcode, index)}
                              value={field.value}
                              error={
                                // @ts-ignore
                                errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.zipcode]?.message &&
                                isErrorShown
                              }
                              placeholder="Enter ZIP code"
                              onChange={field.onChange}
                            />
                          </InputWrapper>
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>

              <FormFieldsContainer>
                <FieldWrapper>
                  <Box direction="row" align="center" justify="between" width="100%">
                    <Box width={{ min: '255px' }}>
                      <Label
                        name={AppBeneficiaryField.allocationPercentage}
                        showRequireIcon
                        isNarrow
                        node={
                          <Box margin={{ top: 'spacing4' }}>
                            <Inscription color="textSecondary" lineHeight="18px" weight={500} size="small">
                              Must be a whole number, and the sum of all beneficiary designations should total
                              100%
                            </Inscription>
                          </Box>
                        }
                      >
                        Allocation percentage
                      </Label>
                    </Box>
                    <ControllerWrapper
                      style={{ flex: 'none' }}
                      {...(isErrorShown &&
                      // @ts-ignore
                      errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.allocationPercentage]?.message
                        ? {
                            errorText: (
                              <Box width={{ max: '320px' }}>
                                {
                                  // @ts-ignore
                                  errors?.[fieldArrayName]?.[index]?.[
                                    AppBeneficiaryField.allocationPercentage
                                  ]?.message
                                }
                              </Box>
                            ),
                          }
                        : {})}
                    >
                      <Controller
                        name={getPath(AppBeneficiaryField.allocationPercentage, index)}
                        control={control}
                        render={({ field }) => (
                          <InputWrapper>
                            <MaskTextInput
                              name={AppBeneficiaryField.allocationPercentage}
                              value={field.value}
                              onChange={(val) => {
                                if (val && Number.isNaN(Number(val))) return;
                                if (Number(val) > maxAllocationPercentage) return;
                                field.onChange(val);
                              }}
                              placeholder="Enter allocation percentage"
                              error={
                                // @ts-ignore
                                errors?.[fieldArrayName]?.[index]?.[AppBeneficiaryField.allocationPercentage]
                                  ?.message && isErrorShown
                              }
                              style={{ width: '160px' }}
                            />
                          </InputWrapper>
                        )}
                      />
                    </ControllerWrapper>
                  </Box>
                </FieldWrapper>
              </FormFieldsContainer>
            </StyledFieldsList>
          </Box>
        ))}

        <Box
          data-testId="AddOneMore"
          pad="spacing24"
          round="container1Round"
          background="canvas"
          border={{
            size: 'small',
            color: 'border2',
          }}
        >
          <Box
            pad="spacing8"
            direction="row"
            align="center"
            onClick={() => {
              append(formatToAppendBeneficiary());
            }}
          >
            <Box margin={{ right: 'spacing4' }}>
              <PlusIcon color="textAccent" size="12px" />
            </Box>
            <Inscription color="textAccent" weight="bold" lineHeight="20px">
              Add One More {typeTitle} Beneficiary
            </Inscription>
          </Box>
        </Box>
      </Box>

      <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
        <AppButton buttonType="secondary" width="160px" testId="beneficiaryModal_cancel" onClick={onClose}>
          Cancel
        </AppButton>

        <Box margin={{ left: 'spacing12' }}>
          <AppButton width="160px" testId="beneficiaryModal_submit" type="submit" onClick={onSubmit}>
            Submit
          </AppButton>
        </Box>
      </Box>
    </>
  );
};

export default AddBeneficiaryForm;
